import Input from "@ui-kit/form/Input/Input.web";
import { useTranslation } from "@shared/utils/internationalization";
import {
  forwardRef,
  useCallback,
  useDeferredValue,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import useUpdateEffect from "@shared/hooks/useUpdateEffect/useUpdateEffect";
import { CardCvcChangeEvent, CardCvcInputProps } from "./CardCvcInput.props";

const CardCvcInput = forwardRef<HTMLInputElement | null, CardCvcInputProps>(
  function CardCvcInput({ onChange }, ref) {
    const { t } = useTranslation("checkout");

    const inputRef = useRef<HTMLInputElement>(null);

    const [cardCvc, setCardCvc] = useState("");

    // Deferred value of card cvc for complementary logic
    // such as onChange callbacks
    const deferredCardCvc = useDeferredValue(cardCvc);

    const onChangeObjCache = useRef<CardCvcChangeEvent>({
      complete: false,
      error: null,
    });

    const isInputComplete = useCallback((cardNumber: string) => {
      if (cardNumber.length >= 3) {
        return true;
      }
      return false;
    }, []);

    // Side effect that calls onChange when the card cvc changes
    useUpdateEffect(() => {
      onChangeObjCache.current = {
        ...onChangeObjCache.current,
        complete: isInputComplete(deferredCardCvc),
      };

      onChange?.(onChangeObjCache.current);
    }, [onChange, isInputComplete, deferredCardCvc]);

    /**
     * Intercept the input event to replace the resulting value with more legible formatting.
     *
     * The exposed `onChange` API of this component is not a true onChange.
     * It is not passed into the input function. Since "input" events behave like React "onChange" events,
     * we intercept the "input" event, but expose an `onChange` API triggered by an "input" event.
     */
    const interceptOnInput = useCallback((e: Event) => {
      // Remove all non-digit characters
      const value = (e.target as HTMLInputElement).value.replace(/\D/g, "");

      // Update state
      setCardCvc(value);
    }, []);

    /**
     * Validates a card security code based on the `cardCvc` state.
     * Fires an onChange event regardless of the outcome
     */
    const validateInput = useCallback(() => {
      let error: CardCvcChangeEvent["error"] = null;

      if (!cardCvc || cardCvc.length < 3) {
        error = {
          type: "validation_error",
          code: "invalid_cvc",
          message: t("Your card's expiration date is incomplete."),
        };
      }

      onChangeObjCache.current = {
        ...onChangeObjCache.current,
        error,
      };

      onChange?.(onChangeObjCache.current);
    }, [t, onChange, cardCvc]);

    /**
     * Add our change event interceptor via addEventListener.
     * This allows other event listeners to be added without being overridden by an explicity `onChange`
     * React element attribute.
     */
    useEffect(() => {
      inputRef.current?.addEventListener("input", interceptOnInput);

      return () =>
        inputRef.current?.removeEventListener("input", interceptOnInput);
    }, [interceptOnInput]);

    /**
     * Add our blur event interceptor via addEventListener.
     *
     * We use the blur event to trigger our validation
     */
    useEffect(() => {
      inputRef.current?.addEventListener("blur", validateInput);

      return () => inputRef.current?.removeEventListener("blur", validateInput);
    }, [validateInput]);

    /**
     * Expose the internal input ref
     */
    useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
      ref,
      () => inputRef.current,
      []
    );

    return (
      <Input
        w="100%"
        h="element.3xl"
        lineHeight="1.2em"
        ref={inputRef}
        placeholder={t("CVC")}
        value={cardCvc}
        variant="unstyled"
        outline="none"
        // @see https://web.dev/learn/forms/payment#help_users_enter_their_payment_details
        minLength={3}
        maxLength={4}
        pattern="[0-9]+"
        type="text"
        inputMode="numeric"
      />
    );
  }
);

export default CardCvcInput;
